import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, ShoppingBag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { brl, errMsg } from "@/lib/api";
import { PROFILE, shopApi, shopToken, hasPromo } from "@/lib/shop";
import { toast } from "sonner";
import { Tracking, ProductDialog } from "@/pages/PublicMenuPage";
import { CouponField } from "@/components/delivery/CouponField";
import { ShopTopNav, ShopStickyBar } from "@/components/shop/ShopNav";
import { ShopHero } from "@/components/shop/ShopHero";
import { ShopSection } from "@/components/shop/ShopProducts";
import { CartPanel } from "@/components/shop/CartPanel";
import { ShipForm } from "@/components/shop/ShipForm";
import { CheckoutDialog } from "@/components/shop/CheckoutDialog";
import { AccountDialog } from "@/components/shop/AccountDialog";
import { InfoDialog, OrdersView } from "@/components/shop/ShopInfo";
import { withGeral } from "@/lib/catalog";

function Footer({ st }) {
  return (
    <footer className="mt-16 border-t border-slate-200 bg-white py-8 text-center text-sm text-slate-500" data-testid="shop-footer">
      <p className="font-semibold text-slate-700">{st.name} - {new Date().getFullYear()}. Todos os direitos reservados</p>
      <p className="mt-1">{[st.cnpj && `CNPJ: ${st.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")}`, st.address, st.telefone].filter(Boolean).join(" | ")}</p>
      <p className="mt-3 text-xs">Plataforma FSCsistemas</p>
    </footer>
  );
}

export default function ShopPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const http = useMemo(() => shopApi(slug), [slug]);
  const saved = JSON.parse(localStorage.getItem(PROFILE) || "{}");
  const [menu, setMenu] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState("inicio");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem(`shop_cart_${slug}`) || "[]"));
  const [ship, setShip] = useState({ type: "delivery", zone: saved.zone || "", addr: saved.addr || {}, point: saved.point || null, quote: null });
  const [coupon, setCoupon] = useState(null);
  const [me, setMe] = useState(null);
  const [prod, setProd] = useState(null);
  const [modal, setModal] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    http.get("").then((r) => setMenu({ ...r.data, ...withGeral(r.data.products || [], r.data.categories || []) })).catch((e) => setError(errMsg(e)));
    if (shopToken.get(slug)) http.get("/account/me").then((r) => setMe(r.data)).catch(() => shopToken.clear(slug));
    const code = params.get("cupom");
    if (code) http.post("/coupon", { code }).then((r) => { setCoupon(r.data); toast.success("Cupom aplicado"); }).catch((e) => toast.error(errMsg(e)));
  }, [http, slug]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { localStorage.setItem(`shop_cart_${slug}`, JSON.stringify(cart)); }, [cart, slug]);
  useEffect(() => { if (menu && !menu.store.delivery_enabled) setShip((s) => ({ ...s, type: "retirada" })); }, [menu]);

  const st = menu?.store;
  const color = st?.brand_color || "#16a34a";
  const products = useMemo(() => (menu?.products || []).filter((p) => p.show_in_delivery !== false), [menu]);
  const featured = products.filter((p) => p.featured);
  const promos = products.filter((p) => hasPromo(p) || p.featured);
  const found = q.trim() ? products.filter((p) => `${p.name} ${p.description || ""}`.toLowerCase().includes(q.trim().toLowerCase())) : [];
  const count = cart.reduce((s, i) => s + i.qty, 0);
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const add = (item) => { setCart((c) => (c.find((i) => i.key === item.key) ? c.map((i) => (i.key === item.key ? { ...i, qty: i.qty + item.qty } : i)) : [...c, item])); toast.success("Adicionado à sacola"); };
  const setQty = (key, d) => setCart((c) => c.map((i) => (i.key === key ? { ...i, qty: i.qty + d } : i)).filter((i) => i.qty > 0));
  const open = (p) => (st.accepting ? setProd(p) : toast.info("Estamos fechados agora. Os pedidos abrem no horário de funcionamento."));
  const goCat = (id) => { setView("inicio"); setQ(""); setTimeout(() => document.getElementById(`cat-${id}`)?.scrollIntoView({ behavior: "smooth" }), 50); };
  const finish = (id) => { setCart([]); setCoupon(null); setModal(""); setDone(id); if (me) http.get("/account/me").then((r) => setMe(r.data)); window.scrollTo(0, 0); };

  if (error) return <div className="grid min-h-screen place-items-center p-6 text-center" data-testid="public-menu-error">{error}</div>;
  if (!menu) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  if (done) return <Tracking slug={slug} id={done} color={color} phone={st.whatsapp} onBack={() => setDone(null)} />;

  const panel = (
    <CartPanel st={st} cart={cart} setQty={setQty} ship={ship} coupon={coupon} color={color} onCalc={() => setModal("calc")}
      onCoupon={() => setModal("coupon")} onRemoveCoupon={() => setCoupon(null)} onCheckout={() => setModal("checkout")} />
  );
  return (
    <div className="min-h-screen bg-slate-50 pb-24 text-slate-900 lg:pb-0" data-testid="shop-page">
      <ShopTopNav view={view} setView={setView} me={me} onAccount={() => setModal("account")} color={color} />
      {view === "inicio" && !q && <ShopHero st={st} color={color} me={me} onInfo={() => setModal("info")} onAccount={() => setModal("account")} />}
      <ShopStickyBar st={st} categories={menu.categories} onCat={goCat} q={q} setQ={setQ} view={view} setView={setView} color={color} />
      <main className="mx-auto grid max-w-7xl gap-8 px-4 py-8 lg:grid-cols-[1fr_400px]">
        <div className="min-w-0 space-y-10">
          {view === "pedidos" ? <OrdersView slug={slug} http={http} me={me} onOpen={setDone} onLogin={() => setModal("account")} color={color} />
            : view === "promocoes" ? (promos.length ? <ShopSection id="promocoes" title="Promoções" items={promos} color={color} onOpen={open} /> : <p className="text-slate-500" data-testid="shop-no-promos">Nenhuma promoção no momento.</p>)
            : q.trim() ? (found.length ? <ShopSection id="busca" title={`Resultados para "${q.trim()}"`} items={found} color={color} onOpen={open} /> : <p className="text-slate-500" data-testid="shop-no-results">Nenhum produto encontrado.</p>)
            : (<>
              <ShopSection id="destaque" title="Destaque" items={featured} color={color} onOpen={open} />
              {menu.categories.map((c) => <ShopSection key={c.id} id={`cat-${c.id}`} title={c.name} description={c.description} items={products.filter((p) => p.category_id === c.id)} color={color} onOpen={open} />)}
            </>)}
        </div>
        <aside className="hidden lg:block"><div className="sticky top-24">{panel}</div></aside>
      </main>
      <Footer st={st} />
      {count > 0 && (
        <button onClick={() => setModal("cart")} className="fixed inset-x-4 bottom-4 z-30 flex items-center justify-between rounded-2xl px-5 py-4 font-semibold text-white shadow-xl lg:hidden" style={{ background: color }} data-testid="shop-mobile-cart-btn">
          <span className="flex items-center gap-2"><ShoppingBag className="h-5 w-5" />Ver sacola ({count})</span><span>{brl(subtotal)}</span>
        </button>
      )}
      <Sheet open={modal === "cart"} onOpenChange={(v) => setModal(v ? "cart" : "")}>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto bg-slate-50 p-3 text-slate-900"><SheetHeader><SheetTitle className="text-slate-900">Sua sacola</SheetTitle></SheetHeader><div className="pt-3">{panel}</div></SheetContent>
      </Sheet>
      <Dialog open={modal === "calc"} onOpenChange={(v) => setModal(v ? "calc" : "")}>
        <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto bg-white text-slate-900" data-testid="shop-calc-modal">
          <DialogHeader><DialogTitle className="text-slate-900">Taxa e tempo de entrega</DialogTitle></DialogHeader>
          <ShipForm st={st} slug={slug} ship={ship} setShip={setShip} color={color} />
          <Button className="w-full text-white" style={{ background: color }} onClick={() => setModal("")} data-testid="shop-calc-confirm">Confirmar</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={modal === "coupon"} onOpenChange={(v) => setModal(v ? "coupon" : "")}>
        <DialogContent className="max-w-md bg-white text-slate-900" data-testid="shop-coupon-modal">
          <DialogHeader><DialogTitle className="text-slate-900">Inserir cupom</DialogTitle></DialogHeader>
          <CouponField slug={slug} coupon={coupon} onChange={(c) => { setCoupon(c); if (c) setModal(""); }} />
        </DialogContent>
      </Dialog>
      <CheckoutDialog open={modal === "checkout"} onOpenChange={(v) => setModal(v ? "checkout" : "")} st={st} slug={slug} http={http} cart={cart} ship={ship} setShip={setShip}
        coupon={coupon} me={me} onLogin={() => setModal("account")} onDone={finish} color={color} />
      <AccountDialog open={modal === "account"} onOpenChange={(v) => setModal(v ? "account" : "")} http={http} me={me} setMe={setMe} st={st} slug={slug} color={color} />
      <InfoDialog open={modal === "info"} onOpenChange={(v) => setModal(v ? "info" : "")} st={st} />
      <ProductDialog p={prod} onClose={() => setProd(null)} onAdd={add} color={color} />
    </div>
  );
}
